import { useEffect } from 'react';
import type { CriticalAiCategory } from '@sr-test-wizard/shared';
import { useCriticalAiStore } from '../../state/criticalAiStore';

interface Props {
  runId: string;
  projectPath: string | null;
  onOpenFile: (relativePath: string) => void;
}

const CATEGORY_LABEL: Record<CriticalAiCategory, string> = {
  reelset: 'Reelset',
  weights: 'Weights',
  paytable: 'Paytable',
  config: 'Config',
  rtp: 'RTP',
  rng: 'RNG',
  chain: 'Chain',
  other: 'Other',
};

/**
 * AI Critical-File Nominator card (Phase 3). For games where critical files are
 * not declared, the model inspects the pulled source, traces the RNG -> config
 * -> math chain, and nominates the payout-affecting files. SHA1s are computed
 * server-side from disk; the table is read back from the persisted result.
 */
export function CriticalFilesAiCard({ runId, projectPath, onOpenFile }: Props) {
  const { result, loading, error, progress, load, nominate } = useCriticalAiStore();

  useEffect(() => {
    void load(runId);
  }, [runId, load]);

  const running =
    progress?.status === 'started' ||
    progress?.status === 'progress' ||
    result?.status === 'running';

  const activity =
    progress?.tool || progress?.message || (running ? 'Analysing the source package...' : null);

  const copySha = (sha: string) => {
    void navigator.clipboard?.writeText(sha).catch(() => {});
  };

  return (
    <div className="card">
      <div className="row">
        <div>
          <strong>Critical Files - AI Nominator</strong>
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            For games with no predefined critical files: the AI inspects the pulled source, traces
            the RNG -&gt; config -&gt; math chain, and nominates payout-affecting files. SHA-1 is
            computed on-disk by the server.
          </p>
        </div>
        <span className="spacer" />
        <button
          className="btn btn-primary"
          disabled={!projectPath || running}
          onClick={() => void nominate()}
          title={!projectPath ? 'Pull the source code first (Phase 3).' : undefined}
        >
          {running ? 'Nominating...' : result?.items?.length ? 'Re-nominate with AI' : 'Nominate critical files with AI'}
        </button>
      </div>

      {running && activity && (
        <div className="muted small mono" style={{ marginTop: 10 }}>
          <span className="spinner-dot" /> {activity}
        </div>
      )}

      {(error || result?.error) && !running && (
        <div className="muted small" style={{ marginTop: 10, color: 'var(--bad)' }}>
          {error || result?.error}
        </div>
      )}

      {result?.chainSummary && !running && (
        <div className="callout" style={{ marginTop: 12 }}>
          <strong className="small">Traced chain</strong>
          <p className="small" style={{ margin: '4px 0 0' }}>{result.chainSummary}</p>
        </div>
      )}

      {result?.items && result.items.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ margin: '0 0 6px' }}>
            Nominated Files ({result.items.length})
            {result.model && (
              <span className="muted small" style={{ marginLeft: 8, fontWeight: 400 }}>
                {result.model}
              </span>
            )}
          </h3>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Category</th>
                  <th>Path</th>
                  <th>SHA-1</th>
                  <th>Why critical</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((item, i) => (
                  <tr key={`${item.path}-${i}`}>
                    <td>{item.filename}</td>
                    <td>
                      <span className={`chip chip-${item.category}`}>
                        {CATEGORY_LABEL[item.category]}
                      </span>
                    </td>
                    <td
                      className="mono small row-clickable"
                      onClick={() => onOpenFile(item.path)}
                      title="Open file"
                    >
                      {item.path}
                    </td>
                    <td
                      className="mono small row-clickable"
                      onClick={() => copySha(item.sha1)}
                      title={`Click to copy. Raw SHA-1: ${item.sha1Raw}`}
                    >
                      {truncate(item.sha1, 16)}
                    </td>
                    <td className="small">{item.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.generatedAt && (
            <div className="muted small" style={{ marginTop: 6 }}>
              Generated {new Date(result.generatedAt).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {!running && !loading && (!result || result.items.length === 0) && !result?.error && (
        <p className="muted small" style={{ marginTop: 12 }}>
          {projectPath
            ? 'No AI nominations yet. Click "Nominate critical files with AI" to inspect the pulled source.'
            : 'Pull the source code first to let the AI inspect the package.'}
        </p>
      )}
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n / 2)}...${s.slice(-n / 2)}`;
}
